"use client"

import * as React from "react"

import { useActiveBranchSlug } from "@/features/auth/hooks/use-active-branch-slug"
import { useActiveWorkspaceSlug } from "@/features/auth/hooks/use-active-workspace-slug"
import { useShowMultiBranchWorkspaceUi } from "@/features/auth/hooks/use-show-multi-branch-workspace-ui"
import type { FinanceReportEntry } from "@/features/reports/model/report-registry-types"
import {
  FinanceReportCatalogContext,
  getFinanceReportsForCatalogContext,
} from "@/features/reports/registry/finance-report-registry"
import { buildBranchAdminUrl } from "@/shared/lib/branch/branch-admin-routing"

export enum FinanceReportsSurfaceKind {
  /** One branch (or the parish as a branch) — the original Finance → Reports catalog. */
  Branch = "branch",
  /** The whole workspace, consolidated across every branch — Global Finance → Reports. */
  Global = "global",
}

export interface FinanceReportsSurfaceValue {
  kind: FinanceReportsSurfaceKind
  /**
   * The reports this surface offers. Not every report can answer every scope: the accounting reports resolve a
   * single branch, so the Global surface offers only the engine-backed ones until consolidation exists. Keeping
   * the list on the surface (rather than filtering at each call site) means a report cannot be reached at a
   * scope it cannot actually answer — including by typing its URL.
   */
  entries: readonly FinanceReportEntry[]
  /** Absolute URL for a path under this surface's reports root, e.g. `/trial-balance/view`. */
  reportHref: (relativePath: string) => string
}

const FinanceReportsSurfaceContext = React.createContext<FinanceReportsSurfaceValue | null>(null)

export function FinanceReportsSurfaceProvider({
  value,
  children,
}: {
  value: FinanceReportsSurfaceValue
  children: React.ReactNode
}) {
  return <FinanceReportsSurfaceContext.Provider value={value}>{children}</FinanceReportsSurfaceContext.Provider>
}

/**
 * Which reports surface the catalog, configure and viewer pages are rendering.
 *
 * Defaults to the branch surface when no provider is present, so the existing Finance → Reports pages behave
 * exactly as before and only the Global Finance pages need to declare anything.
 */
export function useFinanceReportsSurface(): FinanceReportsSurfaceValue {
  const override = React.useContext(FinanceReportsSurfaceContext)
  const branchSlug = useActiveBranchSlug()
  const workspaceSlug = useActiveWorkspaceSlug()
  const showMultiBranchWorkspaceUi = useShowMultiBranchWorkspaceUi()
  const catalogContext = showMultiBranchWorkspaceUi
    ? FinanceReportCatalogContext.MultiBranchBranch
    : FinanceReportCatalogContext.MonoBranch

  return React.useMemo<FinanceReportsSurfaceValue>(
    () =>
      override ?? {
        kind: FinanceReportsSurfaceKind.Branch,
        entries: getFinanceReportsForCatalogContext(catalogContext),
        reportHref: (relativePath: string) =>
          buildBranchAdminUrl(branchSlug, `/reports${relativePath}`, workspaceSlug ?? undefined),
      },
    [catalogContext, override, branchSlug, workspaceSlug],
  )
}

/** The entry for `id`, but only if THIS surface offers it. */
export function useSurfaceReport(id: string | undefined): FinanceReportEntry | undefined {
  const { entries } = useFinanceReportsSurface()
  return React.useMemo(() => entries.find((entry) => entry.id === id), [entries, id])
}
